/* iter55l — Archived Users admin panel.
 *
 * Lists every user that's been archived (soft-deleted) from the Users
 * & Tokens tab. Admin can either:
 *   • Restore — rehydrates the user + all their projects back into the
 *     live tables.
 *   • Purge — permanently deletes the snapshot. Irreversible. Payment
 *     ledger rows are always preserved.
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PanelHeader } from './_shared';
import { RotateCw, Trash2, ArchiveRestore, Loader2, AlertTriangle, Settings, Play } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined,
    { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

// iter55o — Retention config bar. Reads current policy, lets admin
// toggle enabled + set retention_days, and manually run the cleanup pass.
function RetentionConfigBar() {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(90);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    adminApi.getArchiveRetention()
      .then((r) => { setCfg(r); setDays(r.retention_days); setEnabled(r.enabled); })
      .catch(() => { /* silent, first-run default */ });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.setArchiveRetention({ enabled, retention_days: days });
      setCfg(r);
      toast.success(`Retention: ${r.enabled ? `auto-purge after ${r.retention_days} days` : 'DISABLED'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (dry = false) => {
    setRunning(true);
    try {
      const r = await adminApi.runCleanupNow(dry);
      const iter55l = r.iter55l_archives_purged || 0;
      const iter55lPreview = (r.candidates_iter55l_archive_purge || []).length;
      if (dry) {
        toast.info(`Dry run — ${iter55lPreview} archive(s) would be purged.`);
      } else {
        toast.success(`Cleanup pass done — ${iter55l} archive(s) purged.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed.');
    } finally {
      setRunning(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="mt-3 rounded-md border border-border bg-panel-header/40 p-3 text-[12px]"
         data-testid="archive-retention-bar">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        data-testid="archive-retention-toggle"
      >
        <span className="inline-flex items-center gap-2 font-medium">
          <Settings className="h-3.5 w-3.5 text-muted-foreground" />
          Auto-purge policy
        </span>
        <span className="text-[11px] text-muted-foreground">
          {cfg.enabled
            ? `Nightly · archives older than ${cfg.retention_days} days`
            : 'DISABLED — archives never auto-purge'}
        </span>
      </button>

      {expanded && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-[11.5px]">
            <input type="checkbox"
                   checked={enabled}
                   onChange={(e) => setEnabled(e.target.checked)}
                   className="h-3.5 w-3.5"
                   data-testid="archive-retention-enabled" />
            Enable nightly auto-purge
          </label>
          <div>
            <Label className="text-[10.5px]">Retention (days)</Label>
            <Input type="number" min={7} max={3650}
                   value={days}
                   onChange={(e) => setDays(parseInt(e.target.value || '90', 10))}
                   disabled={!enabled}
                   className="h-7 w-24 text-[11.5px]"
                   data-testid="archive-retention-days" />
          </div>
          <Button size="sm" onClick={save} disabled={saving}
                  className="h-7 gap-1 text-[11px]"
                  data-testid="archive-retention-save">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Settings className="h-3 w-3" />}
            Save policy
          </Button>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => runNow(true)}
                    disabled={running}
                    className="h-7 gap-1 text-[11px]"
                    data-testid="archive-run-cleanup-dry">
              Preview
            </Button>
            <Button size="sm" variant="outline" onClick={() => runNow(false)}
                    disabled={running}
                    className="h-7 gap-1 text-[11px]"
                    data-testid="archive-run-cleanup-now">
              {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Run cleanup now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ArchivedUsersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});    // per-user busy flag
  // iter55l — bulk selection for multi-purge.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.archivedUsers();
      setRows(r.users || []);
      // Any selected id no longer in the list is dropped.
      setSelected((prev) => {
        const live = new Set((r.users || []).map((u) => u.user_id));
        return new Set([...prev].filter((id) => live.has(id)));
      });
    } catch {
      toast.error('Could not load archived users.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleOne = (uid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected || someSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.user_id)));
  };

  const totalProjectsInSelection = useMemo(() =>
    rows.filter((r) => selected.has(r.user_id))
        .reduce((sum, r) => sum + (r.archived_project_count || 0), 0),
  [rows, selected]);

  const restore = async (uid) => {
    setBusy((b) => ({ ...b, [uid]: true }));
    try {
      const r = await adminApi.restoreArchivedUser(uid);
      toast.success(`Restored — ${r.restored_projects} project(s) rehydrated.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed.');
    } finally {
      setBusy((b) => ({ ...b, [uid]: false }));
    }
  };

  const purge = async (uid) => {
    setBusy((b) => ({ ...b, [uid]: true }));
    try {
      const r = await adminApi.purgeArchivedUser(uid);
      toast.success(`Purged — ${r.projects_purged} project snapshot(s) permanently deleted.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed.');
    } finally {
      setBusy((b) => ({ ...b, [uid]: false }));
    }
  };

  const bulkPurge = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await adminApi.purgeManyArchivedUsers(ids);
      const bits = [`Purged ${r.purged}/${r.requested} archives`];
      if (r.projects_purged) bits.push(`${r.projects_purged} project snapshot(s) removed`);
      if (r.not_found)       bits.push(`${r.not_found} already gone`);
      toast.success(bits.join(' · '));
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk purge failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <section data-testid="archived-users-panel">
      <PanelHeader
        title="Archived Users"
        subtitle="Soft-deleted users + their projects. Restore or permanently purge — individually or in bulk."
        onRefresh={load}
      />

      {/* iter55o — Nightly auto-purge retention config. */}
      <RetentionConfigBar />

      {/* iter55l — Bulk purge action bar (only when at least one row is selected). */}
      {selected.size > 0 && (
        <div className="mt-3 flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[12px]"
             data-testid="archived-users-bulk-bar">
          <div className="text-destructive font-medium">
            {selected.size} archive{selected.size === 1 ? '' : 's'} selected
            {totalProjectsInSelection > 0 && (
              <span className="ml-1 text-muted-foreground font-normal">
                · {totalProjectsInSelection} project snapshot(s) inside
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-[11.5px]"
                    onClick={() => setSelected(new Set())}
                    data-testid="archived-users-clear-selection">
              Clear
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive"
                        className="h-7 gap-1 text-[11.5px]"
                        disabled={bulkBusy}
                        data-testid="archived-users-bulk-purge-btn">
                  {bulkBusy
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Trash2 className="h-3 w-3" />}
                  Purge {selected.size} selected
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="archived-users-bulk-purge-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="inline-flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    Permanently purge {selected.size} archive{selected.size === 1 ? '' : 's'}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This wipes every selected archive snapshot for good, along with
                    {' '}<b>{totalProjectsInSelection} project snapshot(s)</b>. The
                    users and projects cannot be restored. Payment / royalty ledger
                    rows are always preserved.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={bulkPurge}
                    className="bg-destructive hover:bg-destructive/90"
                    data-testid="archived-users-bulk-purge-confirm">
                    Purge {selected.size} archive{selected.size === 1 ? '' : 's'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-md border border-border bg-card">
        <div className="grid grid-cols-12 gap-2 border-b border-border bg-panel-header/40 px-3 py-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3 flex items-center gap-2">
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={toggleAll}
              disabled={rows.length === 0}
              data-testid="archived-users-select-all"
              aria-label="Select all archived users"
            />
            <span>User</span>
          </div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Archived</div>
          <div className="col-span-1 text-right" title="Projects archived alongside the user">Proj</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="px-3 py-6 text-[12px] text-muted-foreground">
            <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-8 text-center text-[12px] text-muted-foreground"
               data-testid="archived-users-empty">
            No archived users. Users you archive from <b>Users &amp; Tokens</b> will appear here.
          </div>
        ) : rows.map((u) => (
          <div key={u.user_id}
               data-testid={`archived-user-row-${u.user_id}`}
               data-selected={selected.has(u.user_id) ? '1' : '0'}
               className={`grid grid-cols-12 items-center gap-2 border-b border-border px-3 py-2 text-[12.5px] ${
                 selected.has(u.user_id) ? 'bg-destructive/5' : ''
               }`}>
            <div className="col-span-3 flex items-center gap-2 min-w-0">
              <Checkbox
                checked={selected.has(u.user_id)}
                onCheckedChange={() => toggleOne(u.user_id)}
                data-testid={`archived-user-select-${u.user_id}`}
                aria-label={`Select ${u.email || u.user_id}`}
              />
              {u.picture
                ? <img src={u.picture} alt="" className="h-6 w-6 rounded-full border border-border opacity-60" />
                : <div className="h-6 w-6 rounded-full bg-secondary" />}
              <div className="min-w-0">
                <div className="truncate font-medium text-muted-foreground">{u.name || '—'}</div>
                <div className="font-mono-stat text-[10.5px] text-muted-foreground truncate">{u.user_id}</div>
              </div>
            </div>
            <div className="col-span-3 truncate text-[12px] text-muted-foreground">{u.email || '—'}</div>
            <div className="col-span-2 text-[11px]">
              <div>{fmtDate(u.archived_at)}</div>
              {u.archived_reason && (
                <div className="truncate text-[10.5px] italic text-muted-foreground" title={u.archived_reason}>
                  &ldquo;{u.archived_reason}&rdquo;
                </div>
              )}
            </div>
            <div className="col-span-1 text-right font-mono-stat"
                 data-testid={`archived-user-projects-${u.user_id}`}>
              {u.archived_project_count}
            </div>
            <div className="col-span-3 flex items-center justify-end gap-1">
              <Button size="sm" variant="outline"
                      onClick={() => restore(u.user_id)}
                      disabled={busy[u.user_id]}
                      className="h-7 gap-1 text-[11.5px]"
                      data-testid={`archived-user-restore-${u.user_id}`}>
                {busy[u.user_id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArchiveRestore className="h-3 w-3" />}
                Restore
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost"
                          className="h-7 gap-1 text-[11.5px] text-destructive hover:text-destructive"
                          data-testid={`archived-user-purge-${u.user_id}`}
                          disabled={busy[u.user_id]}>
                    <Trash2 className="h-3 w-3" /> Purge
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`archived-user-purge-dialog-${u.user_id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="inline-flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      Permanently purge {u.email || u.user_id}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This wipes the archive snapshot for good — the user and their
                      {' '}<b>{u.archived_project_count} project(s)</b> can never be
                      restored. Payment ledger rows are preserved for accounting.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => purge(u.user_id)}
                      className="bg-destructive hover:bg-destructive/90"
                      data-testid={`archived-user-purge-confirm-${u.user_id}`}>
                      Purge forever
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
        <RotateCw className="h-3 w-3" />
        Restoring rehydrates the user + all their project snapshots atomically.
        Purge is irreversible.
      </div>
    </section>
  );
}
